import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useCategories, useNavPages } from "@/lib/brands";

const PAGES = [
  { to: "/expert-help", label: "Expert Help" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { data: categories = [] } = useCategories();
  const { data: navPages = [] } = useNavPages();

  return (
    <header className="sticky top-0 z-40 border-b border-hair bg-paper/95 backdrop-blur-sm">
      {/* Top row: logo + pages */}
      <div className="mx-auto flex max-w-desk items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="site-logo" className="flex items-baseline gap-2" onClick={() => setOpen(false)}>
          <span className="font-serif text-xl font-semibold tracking-tight text-ink">
            It&apos;s Genie Assist
          </span>
          <span className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-accent sm:inline">
            Independent
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Pages">
          {PAGES.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-widest transition-colors hover:text-accent ${
                  isActive ? "text-accent" : "text-muted"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Section bar: all categories, one tap away (scrolls horizontally on mobile) */}
      <div className="border-t border-hair" data-testid="category-bar">
        <nav
          aria-label="Categories"
          className="mx-auto flex max-w-desk items-center gap-5 overflow-x-auto px-5 py-2.5 sm:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {categories.map((c) => (
            <NavLink
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`nav-cat-${c.slug}`}
              className={({ isActive }) =>
                `whitespace-nowrap font-mono text-[11px] uppercase tracking-widest transition-colors hover:text-accent ${
                  isActive ? "text-accent" : "text-muted"
                }`
              }
            >
              {c.name}
            </NavLink>
          ))}
          {navPages.map((p) => (
            <NavLink
              key={p.slug}
              to={p.system ? `/${p.slug}` : `/p/${p.slug}`}
              data-testid={`nav-page-${p.slug}`}
              className={({ isActive }) =>
                `whitespace-nowrap font-mono text-[11px] uppercase tracking-widest transition-colors hover:text-accent ${
                  isActive ? "text-accent" : "text-muted"
                }`
              }
            >
              {p.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Mobile menu: pages */}
      {open && (
        <nav className="border-t border-hair bg-paper lg:hidden" aria-label="Mobile" data-testid="mobile-menu">
          <ul className="mx-auto max-w-desk px-5 py-2 sm:px-8">
            {PAGES.map((n) => (
              <li key={n.to} className="border-b border-hair last:border-b-0">
                <NavLink
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block py-3 font-mono text-sm uppercase tracking-widest ${
                      isActive ? "text-accent" : "text-ink"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}
